/**
 * Step 9 · Done (ux-flows §2 step 9, design-spec/07 plate 08) — and the §5 already-signed wall.
 *
 * TWO FACES, one route, chosen by whether THIS VISIT produced the signature:
 *
 *  - **Just signed** (`signResult` is in the ephemeral store): the full confirmation — the seal,
 *    "You've given your word.", what the candidate receives, their verification code, the soft hook.
 *  - **Revisited** (no `signResult` — a refresh, a new tab, the link opened next week): the calm
 *    already-signed page (mock edge state C). The re-entrant link "resolves calmly forever, never
 *    a 404", and this is the wall it lands on.
 *
 * **Why the split is honest rather than convenient.** The referee `/context` wire carries NO
 * `verificationCode` and NO `signedAt` — the sign response is the only place the SPA is ever told
 * either. So on a revisit there is genuinely nothing to show, and the mock's "signed and delivered
 * on 14 July 2026" cannot be rendered without inventing a date. It isn't rendered. The durable copy
 * of both facts is the signed-copy email, which is where this page points.
 *
 * ## "Email me a copy of what I signed" — the mock's control is NOT built, deliberately
 *
 * There is no endpoint for it. The referee paths are exactly eleven and none of them sends the
 * signed PDF; WP-B15b deliberately kept a presigned URL out of the `notifications` jsonb because a
 * chokepoint-free link to the paid artifact readable by anything with SELECT on that table is a
 * side door around the paywall. Building it properly is a backend work package (a referee-
 * authenticated verb, a tokenized link exchanged for a short-TTL presigned GET, its own rate limit
 * and abuse story) — not something to improvise from the SPA, and shipping the button wired to
 * nothing is worse than not shipping it.
 *
 * So the control is omitted and the screen states what ACTUALLY happens instead, which turns out to
 * serve the same purpose the mock's build note gives it ("record + trust — the referee's post-sign
 * access point"): the render job already enqueues a `SignedCopy` email to the signer's address
 * carrying their verification code, the verify URL and their revoke link. That email is real, it is
 * automatic, and it is the only place the revoke link ever appears — which is also why there is no
 * revoke link on this screen (platform §7: the revoke link travels IN the signed-copy email; the
 * revoke endpoint itself is Phase 6). What the email does NOT carry is the letter or the PDF, so
 * the copy here promises exactly that and no more.
 *
 * The PDF renders ASYNCHRONOUSLY, after the sign transaction commits — so nothing here may promise
 * an instantly-available document, and the email may lag by seconds. The copy says "shortly".
 */
import { useEffect, useRef } from 'react';
import { Seal } from '@/components/Seal';
import { useSessionStore } from '@/session/store';
import { useSession } from '@/session/useSession';

export function Done() {
  const { context } = useSession();
  const signResult = useSessionStore((s) => s.signResult);
  const candidateName = context?.candidate.name ?? 'them';

  return signResult ? (
    <JustSigned candidateName={candidateName} verificationCode={signResult.verificationCode} />
  ) : (
    <AlreadySigned candidateName={candidateName} />
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * The confirmation — the payoff
 * ---------------------------------------------------------------------------------------------- */

function JustSigned({
  candidateName,
  verificationCode,
}: {
  candidateName: string;
  verificationCode: string;
}) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  // Announce the arrival by moving focus to the confirmation — see the heading's note below.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center">
      {/* Static, already-stamped: the ceremony played on the Sign screen and this is its result.
          Re-animating it here would stamp the same seal twice — and it keeps framer-motion out of
          the one screen a returning referee lands on. */}
      <Seal size="done" label="Verified" className="mb-4" />

      {/*
        Reached by an AUTOMATIC navigation at the end of the ceremony, not by a click — so without
        help a screen-reader user gets no signal at all that the most important thing they came here
        to do actually happened. Two belts, because this is the one moment worth them:

         - the live region WRAPS the heading (never `role="status"` ON it: that REPLACES the
           implicit heading role rather than adding to it — axe: aria-allowed-role — and would drop
           the confirmation out of heading navigation entirely);
         - focus is moved to the heading, which announces it even where a region rendered on arrival
           does not, and puts the keyboard user at the top of the new screen rather than on <body>.

        `tabIndex={-1}` makes it focusable without putting it in the tab order.
      */}
      <div role="status">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="font-display text-[24px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink focus:outline-none"
        >
          You&rsquo;ve given your word.
        </h1>
      </div>
      <p className="mt-2.5 max-w-[30ch] font-body text-[14px] leading-[1.55] text-ink-70 text-pretty">
        That means a lot. We&rsquo;ve delivered your reference to {candidateName}. You&rsquo;re all done — you
        can close this page.
      </p>

      <DeliveryCard candidateName={candidateName} verificationCode={verificationCode} />

      {/* The truth in place of the mock's unbuilt "Email me a copy" control — see the module
          remarks. It promises only what the SignedCopy email actually contains, and "shortly"
          rather than "now" because the PDF renders after the signature commits. */}
      <p className="mt-3.5 w-full rounded-md border border-line bg-surface-tint px-3.5 py-3 text-left font-body text-[12px] leading-[1.5] text-ink-70">
        We&rsquo;ll email you a copy of this for your records shortly — it has your reference number and a
        link to withdraw the reference if you ever change your mind. Worth keeping.
      </p>

      <ViralHook />
    </div>
  );
}

/**
 * What the candidate receives, and the code that proves it.
 *
 * The verification code is printed VERBATIM in its canonical form: 24 uppercase Crockford-Base32
 * characters, unhyphenated. The PDF groups it for print and the email groups it for reading, but
 * those are their presentation of the same value — the canonical string is what a verify lookup is
 * keyed on, so what is shown here is what can be copied and used. `break-all` because 24 unbroken
 * characters must wrap on a 320px screen rather than push the card sideways.
 */
function DeliveryCard({
  candidateName,
  verificationCode,
}: {
  candidateName: string;
  verificationCode: string;
}) {
  return (
    <div className="mt-4 flex w-full items-center gap-3 rounded-md border border-line bg-surface p-3 text-left shadow-sm">
      <Seal size="mini" label="BMW" />
      <div className="min-w-0">
        <div className="font-display text-[12.5px] font-semibold leading-[1.35] text-ink">
          Your signed letter is in {candidateName}&rsquo;s ledger
        </div>
        <div className="mt-1 font-display text-[10.5px] font-medium leading-[1.4] tracking-[0.03em] text-ink-quiet">
          Verified by By My Word · Ref{' '}
          <span className="break-all font-semibold text-ink-70">{verificationCode}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * The soft hook — "one quiet card, never pushy, no urgency. This is the only place the product
 * markets itself to the referee." An ordinary external link, and nothing that tracks them: the
 * hardening rule is no third-party tracking, and this screen is the one most tempting to measure.
 */
function ViralHook() {
  return (
    <div className="mt-3 w-full rounded-md border border-line bg-surface-tint px-3.5 py-3 text-left">
      <p className="font-body text-[12px] leading-[1.5] text-ink-70">
        Need references yourself one day? <b className="font-semibold text-ink">By My Word</b> makes it easy.{' '}
        <a
          href="https://bymyword.com"
          target="_blank"
          rel="noreferrer"
          className="whitespace-nowrap font-display text-[11.5px] font-semibold text-coral-text underline-offset-2 hover:underline"
        >
          Have a look &rsaquo;
        </a>
      </p>
    </div>
  );
}

/* ---------------------------------------------------------------------------------------------- *
 * The already-signed wall (§5 / mock edge state C)
 * ---------------------------------------------------------------------------------------------- */

/**
 * Calm, not an error — they did nothing wrong, they just came back.
 *
 * No verification code and no signed-on date: neither is on the context wire, and this page will
 * not invent either. It points at the email that really does carry them. No "Email me a copy"
 * control for the same reason as the confirmation face — there is no endpoint behind it.
 */
function AlreadySigned({ candidateName }: { candidateName: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center">
      <Seal size="done" label="Verified" className="mb-4" />
      <h1 className="font-display text-[22px] font-semibold leading-[1.2] tracking-[-0.02em] text-ink text-balance">
        You&rsquo;ve already given your word.
      </h1>
      <p className="mt-2.5 max-w-[32ch] font-body text-[14px] leading-[1.55] text-ink-70 text-pretty">
        This reference is signed and delivered to {candidateName}. Nothing more is needed from you.
      </p>
      <p className="mt-5 max-w-[34ch] font-body text-[12px] leading-[1.5] text-ink-70">
        We emailed you a copy when you signed it — that&rsquo;s where your reference number and the link to
        withdraw it live.
      </p>
      <p className="mt-6 font-display text-[11.5px] font-medium text-ink-70">You can close this page.</p>
    </div>
  );
}
